import { useMemo } from 'react';

import { Avatar } from '@/ui/Avatars/Avatar';
import { AvatarStack } from '@/ui/Avatars/AvatarStack';
import { ChainAvatar, chainAvatarRadius } from '@/ui/Avatars/ChainAvatar';

import { fontStyles } from '../../../../styles/typography';
import { classNames } from '../../../../utils/classnames';
import { Asset } from '../../../../utils/discovery/types';
import { truncate } from '../../../../utils/string';

interface UnifiedTokenNameCellProps {
  asset: Asset;
}

const MAX_NETWORK_LOGOS = 10;

/**
 * UnifiedTokenNameCell - Displays unified token information for parent asset row
 *
 * Shows the token logo, name, and symbol. On hover, reveals network icons
 * with "X networks" text.
 */
export function UnifiedTokenNameCell({ asset }: UnifiedTokenNameCellProps) {
  // Use the first token (highest market cap) for the link
  const primaryToken = asset.tokens?.[0];

  // Get unique chain IDs from all tokens in order (deduplicated)
  const uniqueChainIds = useMemo(() => {
    const chainIds = asset.tokens.map((t) => t.token.chainId);
    // Use array to maintain order, filter duplicates
    return chainIds.filter((id, index) => chainIds.indexOf(id) === index);
  }, [asset.tokens]);

  // Safety check - if no tokens available, return null
  if (!primaryToken || !asset.tokens || asset.tokens.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-[42px] flex justify-center">
        <Avatar
          src={asset.logo}
          fallbackSrc="/images/chains/none.svg"
          alt={asset.displayName}
          size={32}
        />
      </div>
      <div className="flex flex-col">
        <span
          className={fontStyles({
            variant: 'LABEL_MD',
            class: [
              'leading-4',
              'overflow-hidden',
              'text-ellipsis',
              'whitespace-nowrap',
              'max-w-[130px]',
              'tablet:max-w-[200px]',
            ],
          })}
        >
          {asset.displayName.length > 24
            ? truncate(asset.displayName, 24, true)
            : asset.displayName}
        </span>
        <div className="relative overflow-hidden h-5 w-40 tablet:w-64 z-10">
          <div
            className={classNames(
              'transition-transform',
              'duration-200',
              'absolute',
              'top-0',
              'left-0',
              '-translate-y-5.5',
              'group-hover:translate-y-0',
              'ease-out',
              'flex',
              'flex-col',
              'gap-1',
            )}
          >
            {/* Network icons + "X networks" - visible on hover */}
            <div
              className={fontStyles({
                variant: 'BODY_SM',
                class: [
                  'text-token-gray-800',
                  'leading-[18px]',
                  'opacity-0',
                  'group-hover:opacity-100',
                  'transition-opacity',
                  'duration-200',
                  'ease-out',
                  'flex',
                  'items-center',
                  'gap-1.5',
                ],
              })}
            >
              <AvatarStack
                size={16}
                overlap={5}
                borderRadius={chainAvatarRadius}
                maxItems={MAX_NETWORK_LOGOS}
                badgeClassNames="bg-token-gray-400 border-special-border-avatar text-token-gray-800"
              >
                {uniqueChainIds.map((chainId) => (
                  <ChainAvatar key={chainId} size={16} chainId={chainId} />
                ))}
              </AvatarStack>
              <div>
                {uniqueChainIds.length} {uniqueChainIds.length === 1 ? 'network' : 'networks'}
              </div>
            </div>
            {/* Symbol - visible by default */}
            <div
              className={fontStyles({
                variant: 'BODY_SM',
                class: [
                  'text-token-gray-800',
                  'leading-[18px]',
                  'opacity-100',
                  'group-hover:opacity-0',
                  'transition-opacity',
                  'duration-200',
                  'ease-out',
                ],
              })}
            >
              {asset.symbol}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
